/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { StaffSurveyCategory } from '../models/StaffSurveyCategory';
import type { StaffSurveyCodeRequest } from '../models/StaffSurveyCodeRequest';
import type { StaffSurveyForm } from '../models/StaffSurveyForm';
import type { StaffSurveyFormResponse } from '../models/StaffSurveyFormResponse';
import type { StaffSurveyOverview } from '../models/StaffSurveyOverview';
import type { StaffSurveyOverviewDetail } from '../models/StaffSurveyOverviewDetail';
import type { StaffSurveyQuestion } from '../models/StaffSurveyQuestion';
import type { StaffSurveyQuestionList } from '../models/StaffSurveyQuestionList';
import type { StaffSurveySubmission } from '../models/StaffSurveySubmission';
import type { StaffSurveySubmissionDetails } from '../models/StaffSurveySubmissionDetails';
import type { StaffSurveySubmissionWithCode } from '../models/StaffSurveySubmissionWithCode';
import { request as __request } from '../core/request';

export class StaffSurveyService {

    /**
     * get list of staff survey category
     * @returns StaffSurveyCategory The fetch staff survey category list
     * @throws ApiError
     */
    public static async getStaffSurveyCategories({
companyId,
}: {
/** The companyId **/
companyId: number,
}): Promise<Array<StaffSurveyCategory>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category`,
        });
        return result.body;
    }

    /**
     * get list of staff survey forms
     * @returns StaffSurveyCategory The fetch staff survey form list
     * @throws ApiError
     */
    public static async createStaffSurveyCategory({
companyId,
requestBody,
}: {
/** The companyId **/
companyId: number,
requestBody: StaffSurveyCategory,
}): Promise<StaffSurveyCategory> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/staff-survey/category`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * update staff survey category
     * @returns StaffSurveyCategory The fetch staff survey form list
     * @throws ApiError
     */
    public static async updateStaffSurveyCategory({
companyId,
requestBody,
}: {
/** The companyId **/
companyId: number,
requestBody: StaffSurveyCategory,
}): Promise<StaffSurveyCategory> {
        const result = await __request({
            method: 'PUT',
            path: `/companies/${companyId}/staff-survey/category`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * delete staff survey category
     * @returns any deleted staff survey category
     * @throws ApiError
     */
    public static async deleteStaffSurveyCategory({
companyId,
requestBody,
}: {
/** The companyId **/
companyId: number,
requestBody: StaffSurveyCategory,
}): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/companies/${companyId}/staff-survey/category`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * get list of staff survey forms
     * @returns StaffSurveyForm The fetch staff survey form list
     * @throws ApiError
     */
    public static async getStaffSurveyFormList({
companyId,
slug,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
}): Promise<Array<StaffSurveyForm>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/list`,
        });
        return result.body;
    }

    /**
     * get list of staff survey forms
     * @returns StaffSurveyFormResponse The fetch staff survey form list
     * @throws ApiError
     */
    public static async createStaffSurveyForm({
companyId,
slug,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
requestBody: StaffSurveyForm,
}): Promise<StaffSurveyFormResponse> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/staff-survey/category/${slug}`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * get staff active survey forms
     * @returns StaffSurveyFormResponse The fetch staff active survey form
     * @throws ApiError
     */
    public static async getActiveStaffForm({
companyId,
slug,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
}): Promise<Array<StaffSurveyFormResponse>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/active`,
        });
        return result.body;
    }

    /**
     * get list of staff survey forms
     * @returns StaffSurveyFormResponse The fetch staff survey form
     * @throws ApiError
     */
    public static async getStaffSurveyForm({
companyId,
slug,
formId,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
}): Promise<Array<StaffSurveyFormResponse>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}`,
        });
        return result.body;
    }

    /**
     * update staff survey form
     * @returns any The fetch staff survey form list
     * @throws ApiError
     */
    public static async updateStaffSurveyForm({
companyId,
slug,
formId,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
requestBody: StaffSurveyForm,
}): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * archive staff survey form
     * @returns any Successfully archived survey form
     * @throws ApiError
     */
    public static async archiveStaffSurveyForm({
companyId,
slug,
formId,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
}): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}`,
        });
        return result.body;
    }

    /**
     * update staff survey question order
     * @returns any The fetch staff survey form order
     * @throws ApiError
     */
    public static async updateStaffSurveyQuestionOrder({
companyId,
slug,
formId,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
requestBody: StaffSurveyQuestionList,
}): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}/sort`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * update staff survey question
     * @returns any The fetch staff survey form
     * @throws ApiError
     */
    public static async updateStaffSurveyQuestion({
companyId,
slug,
formId,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
requestBody: StaffSurveyQuestion,
}): Promise<any> {
        const result = await __request({
            method: 'PUT',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}/question`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * create staff survey questions
     * @returns any The fetch staff survey form
     * @throws ApiError
     */
    public static async createStaffSurveyQuestion({
companyId,
slug,
formId,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
requestBody: StaffSurveyQuestion,
}): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}/question`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * archive staff survey question
     * @returns any Successfully archived survey question
     * @throws ApiError
     */
    public static async archiveStaffSurveyQuestion({
companyId,
slug,
formId,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The staff survey form id **/
formId: number,
requestBody: StaffSurveyQuestion,
}): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/companies/${companyId}/staff-survey/category/${slug}/form/${formId}/question`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * create staff survey submission
     * @returns any The fetch staff survey form
     * @throws ApiError
     */
    public static async createStaffSurveySubmission({
companyId,
slug,
requestBody,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
requestBody: StaffSurveySubmission,
}): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/staff-survey/category/${slug}/submission`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * get staff survey overview details
     * @returns StaffSurveySubmissionDetails The fetch staff active survey form
     * @throws ApiError
     */
    public static async getStaffSurveySubmissionDetails({
companyId,
slug,
submissionId,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** The submissionId **/
submissionId: number,
}): Promise<Array<StaffSurveySubmissionDetails>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/submission`,
            query: {
                'submissionId': submissionId,
            },
        });
        return result.body;
    }

    /**
     * get staff survey submission list
     * @returns StaffSurveySubmission The fetch staff survey submission list
     * @throws ApiError
     */
    public static async getStaffSurveySubmissionList({
companyId,
slug,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
}): Promise<Array<StaffSurveySubmission>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/submission/list`,
        });
        return result.body;
    }

    /**
     * get staff survey overview list
     * @returns StaffSurveyOverview The fetch staff survey overview list
     * @throws ApiError
     */
    public static async getStaffSurveyOverviewList({
companyId,
slug,
beginAt,
endAt,
}: {
/** The companyId **/
companyId: number,
/** Staff Survey Category slug **/
slug: string,
/** Start date (YYYY-MM-DD or RFC3339) **/
beginAt?: string,
/** End date (YYYY-MM-DD or RFC3339) **/
endAt?: string,
}): Promise<Array<StaffSurveyOverview>> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/overview`,
            query: {
                'beginAt': beginAt,
                'endAt': endAt,
            },
        });
        return result.body;
    }

    /**
     * Get staff survey overview details with submissions and responses
     * @returns StaffSurveyOverviewDetail Successfully retrieved survey overview details
     * @throws ApiError
     */
    public static async getStaffSurveyOverviewDetails({
companyId,
slug,
overviewId,
beginAt,
endAt,
}: {
/** The company ID **/
companyId: number,
/** Staff survey category slug **/
slug: string,
/** The survey overview ID **/
overviewId: number,
/** Start date (YYYY-MM-DD or RFC3339) **/
beginAt?: string,
/** End date (YYYY-MM-DD or RFC3339) **/
endAt?: string,
}): Promise<StaffSurveyOverviewDetail> {
        const result = await __request({
            method: 'GET',
            path: `/companies/${companyId}/staff-survey/category/${slug}/overview/details`,
            query: {
                'overviewId': overviewId,
                'beginAt': beginAt,
                'endAt': endAt,
            },
        });
        return result.body;
    }

    /**
     * get list of staff survey forms
     * @returns StaffSurveyFormResponse The fetch staff survey form by code
     * @throws ApiError
     */
    public static async getStaffSurveyFormByCode({
code,
}: {
code: string,
}): Promise<Array<StaffSurveyFormResponse>> {
        const result = await __request({
            method: 'GET',
            path: `/staff-survey-form`,
            query: {
                'code': code,
            },
        });
        return result.body;
    }

    /**
     * create staff survey submission with code
     * @returns any The fetch staff survey form
     * @throws ApiError
     */
    public static async createStaffSurveySubmissionWithCode({
requestBody,
}: {
requestBody: StaffSurveySubmissionWithCode,
}): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/staff-survey-submission`,
            body: requestBody,
        });
        return result.body;
    }

    /**
     * create staff survey submission with code
     * @returns any The fetch staff survey form
     * @throws ApiError
     */
    public static async sendStaffSurveyLink({
companyId,
requestBody,
}: {
companyId: number,
requestBody: StaffSurveyCodeRequest,
}): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/companies/${companyId}/send-survey-link`,
            body: requestBody,
        });
        return result.body;
    }

}